/**
 * 添付のPDF化(2026-08-26)。
 * - imageToA4Pdf: JPEG/PNG を A4(縦)のPDFに変換する。画像は余白内に収まるよう
 *   縦横比を保って縮小し、中央に配置する(書類として枠内にしっかり収まるように)
 * - convertImagesToPdf: 完了(ロック)時に、取引の添付4種のうち画像のものをPDFへ
 *   変換してStorageに保存し、差し替え後の配列を返す
 */
import { PDFDocument } from 'pdf-lib';
import type { StoredFile, Transaction } from '@core/lib/types';
import { getFileUrl, uploadFiles } from './attachments';

/** A4縦(ポイント)と余白 */
const A4 = { width: 595.28, height: 841.89 };
const MARGIN = 40;

export async function imageToA4Pdf(bytes: Uint8Array, contentType: string): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const image =
    contentType === 'image/png' ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
  const page = doc.addPage([A4.width, A4.height]);
  const maxW = A4.width - MARGIN * 2;
  const maxH = A4.height - MARGIN * 2;
  const scale = Math.min(maxW / image.width, maxH / image.height, 1);
  const w = image.width * scale;
  const h = image.height * scale;
  page.drawImage(image, {
    x: (A4.width - w) / 2,
    y: (A4.height - h) / 2,
    width: w,
    height: h,
  });
  return doc.save();
}

const IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);
export const ATTACHMENT_KINDS = ['attachments', 'receipts', 'received_receipts', 'settlement_receipts'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

/**
 * 取引の添付のうち画像(JPEG/PNG)をPDFに変換し、差し替え後の配列だけを返す。
 * 変換に失敗したファイルは元のまま残す(完了操作を妨げない)
 */
export async function convertImagesToPdf(
  transaction: Transaction,
  userId: string,
): Promise<Partial<Record<AttachmentKind, StoredFile[]>>> {
  const patch: Partial<Record<AttachmentKind, StoredFile[]>> = {};
  for (const kind of ATTACHMENT_KINDS) {
    const list = (transaction[kind] ?? []) as StoredFile[];
    if (!list.some((f) => IMAGE_TYPES.has(f.content_type))) continue;
    const next: StoredFile[] = [];
    let changed = false;
    for (const f of list) {
      if (!IMAGE_TYPES.has(f.content_type)) {
        next.push(f);
        continue;
      }
      try {
        const url = await getFileUrl(f.path);
        const res = await fetch(url);
        const bytes = new Uint8Array(await res.arrayBuffer());
        const pdfBytes = await imageToA4Pdf(bytes, f.content_type);
        const base = f.name.replace(/\.[^.]+$/, '');
        const pdfFile = new File([new Uint8Array(pdfBytes)], `${base}.pdf`, { type: 'application/pdf' });
        const [stored] = await uploadFiles(transaction.id, kind, [pdfFile], userId);
        next.push(stored!);
        changed = true;
      } catch (e) {
        // 変換失敗は元のまま残す(完了操作を妨げない)。原因調査用にログだけ出す
        console.error('PDF変換に失敗:', f.name, e);
        next.push(f);
      }
    }
    if (changed) patch[kind] = next;
  }
  return patch;
}
